/* SignupSteps.jsx · per-step content components for SignupWizard.
 *
 * Extracted from the monolithic /pages/SignupWizard.jsx (P2 component
 * breakdown). Each step is a focused presentational component that
 * receives the form state, validation errors and update callbacks via
 * props · the parent wizard still owns the form/step/validation logic.
 *
 * Exports: Step1Identity, Step2Account, Step3Location, Step4Contact,
 *          Step5Review. */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check, Mail, User, Globe, Phone, ShieldCheck,
  Building2, MapPin, Cake, AlertCircle,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

import { Field, baseInputCls, baseSelectCls, TITLES } from "./SignupShared";

const stepMotion = {
  initial: { opacity: 0, x: 14 },
  animate: { opacity: 1, x: 0 },
  exit:    { opacity: 0, x: -14 },
  transition: { duration: 0.22 },
};

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

/* ─── STEP 1 · Identity ── */
export function Step1Identity({ form, setForm, errors }) {
  const { t } = useTranslation();
  return (
    <motion.div key="s1" {...stepMotion}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={t("signup.fields.title", "Title")} error={errors.title} required>
          <select data-testid="signup-title" className={baseSelectCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}>
            {TITLES.map((opt) => <option key={opt.value} value={opt.value}>{t(`signup.titles.${opt.value}`, opt.label)}</option>)}
          </select>
        </Field>
        <Field label={t("signup.fields.firstName", "First name")} error={errors.firstname} testid="signup-firstname" required>
          <Input data-testid="signup-firstname" className={baseInputCls} value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} placeholder={t("signup.fields.firstNamePh", "Jane")} autoFocus />
        </Field>
        <Field label={t("signup.fields.lastName", "Last name")} error={errors.lastname} testid="signup-lastname" required>
          <Input data-testid="signup-lastname" className={baseInputCls} value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} placeholder={t("signup.fields.lastNamePh", "Trader")} />
        </Field>
      </div>
      <p className="mt-4 text-[12px] text-[color:var(--wiz-text-3)] leading-relaxed">
        {t("signup.fields.idHint", "Use the spelling exactly as it appears on your government-issued ID. We'll only ever contact you using this name.")}
      </p>
    </motion.div>
  );
}

/* ─── STEP 2 · Account ── */
export function Step2Account({ form, setForm, errors }) {
  const { t } = useTranslation();
  const accountTypes = [
    { v: "Private",   labelKey: "individual", IconCmp: User,      hintKey: "individualHint" },
    { v: "Corporate", labelKey: "corporate",  IconCmp: Building2, hintKey: "corporateHint" },
  ];
  return (
    <motion.div key="s2" {...stepMotion}>
      <div className="space-y-4">
        <Field label={t("signup.fields.email", "Email address")} error={errors.email} testid="signup-email" required hint={t("signup.fields.emailHint", "We'll send your verification link here.")}>
          <div className="relative">
            <Input
              data-testid="signup-email"
              type="email"
              className={baseInputCls}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("signup.fields.emailPh", "you@example.com")}
              autoFocus
            />
          </div>
        </Field>
        <Field label={t("signup.fields.accountHolderType", "Account holder type")} required>
          <div className="grid grid-cols-2 gap-3">
            {accountTypes.map(({ v, labelKey, IconCmp, hintKey }) => {
              const active = form.type === v;
              return (
                <button
                  type="button"
                  key={v}
                  data-testid={`signup-type-${v.toLowerCase()}`}
                  onClick={() => setForm({ ...form, type: v })}
                  className={`wiz-tile text-left p-4 ${active ? "is-active" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <IconCmp size={16} className={active ? "text-brand" : "text-[color:var(--wiz-text-3)]"} />
                    <span className="text-[13.5px] font-semibold text-[color:var(--wiz-text-1)]">{t(`signup.fields.${labelKey}`, labelKey)}</span>
                    {active && <Check size={14} className="ml-auto text-[color:var(--brand)]" strokeWidth={3} />}
                  </div>
                  <div className="mt-1 text-[12px] text-[color:var(--wiz-text-3)] leading-snug">{t(`signup.fields.${hintKey}`, hintKey)}</div>
                </button>
              );
            })}
          </div>
        </Field>
        {form.type === "Corporate" && (
          <Field label={t("signup.fields.companyName", "Company name")} error={errors.corporatename} testid="signup-corporatename" required>
            <Input data-testid="signup-corporatename" className={baseInputCls} value={form.corporatename} onChange={(e) => setForm({ ...form, corporatename: e.target.value })} placeholder={t("signup.fields.companyNamePh", "ACME Holdings Ltd")} />
          </Field>
        )}
      </div>
    </motion.div>
  );
}

/* ─── STEP 3 · Location ── */
export function Step3Location({ form, setForm, errors, countries }) {
  const { t } = useTranslation();
  return (
    <motion.div key="s3" {...stepMotion}>
      <div className="space-y-4">
        <Field label={t("signup.fields.country", "Country of residence")} error={errors.country_id} required>
          <select data-testid="signup-country" className={baseSelectCls} value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value, phone_prefix_id: e.target.value })} autoFocus>
            <option value="">{t("signup.fields.countryPh", "— Select your country —")}</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.phone_prefix ? `  (${c.phone_prefix})` : ""}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("signup.fields.city", "City")} error={errors.city} testid="signup-city" required>
            <Input data-testid="signup-city" className={baseInputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("signup.fields.cityPh", "Nairobi")} />
          </Field>
          <Field label={t("signup.fields.address", "Street address")} error={errors.address} testid="signup-address" required>
            <Input data-testid="signup-address" className={baseInputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("signup.fields.addressPh", "123 Main Street, Apt 4B")} />
          </Field>
        </div>
      </div>
      <p className="mt-4 text-[12px] text-[color:var(--wiz-text-3)] leading-relaxed">
        {t("signup.fields.addressHint", "Use the address on your most recent utility bill or government ID · matches our KYC rules.")}
      </p>
    </motion.div>
  );
}

/* ─── STEP 4 · Contact + DOB ── */
export function Step4Contact({ form, setForm, errors, countries, pickedPrefix }) {
  const { t } = useTranslation();
  const months = t("signup.months", { returnObjects: true, defaultValue: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] });
  // Explicit phone-row class — we deliberately DON'T reuse
  // baseSelectCls / baseInputCls here because they both bake in
  // `w-full`, which collides with the per-cell width utilities we need
  // for the prefix + number flex row (Tailwind doesn't deduplicate
  // conflicting width classes · the CSS source order decides the
  // winner, which has been collapsing the row in production).
  const phoneSelect = "wiz-select h-12 px-3 text-[14px] font-medium";
  const phoneInput  = "wiz-input  h-12 px-4 text-[15px] font-medium tracking-wide";
  return (
    <motion.div key="s4" {...stepMotion}>
      <div className="space-y-4">
        <Field label={t("signup.fields.phone", "Phone number")} error={errors.phonenumber} testid="signup-phone" required hint={t("signup.fields.phoneHint", "Used for 2-factor authentication on withdrawals.")}>
          <div className="grid gap-2" style={{ gridTemplateColumns: "minmax(110px, 130px) 1fr" }}>
            <select
              data-testid="signup-phone-prefix"
              className={phoneSelect}
              value={form.phone_prefix_id}
              onChange={(e) => setForm({ ...form, phone_prefix_id: e.target.value })}
              aria-label={t("signup.fields.callingCode", "Country phone prefix")}
            >
              <option value="">{t("signup.fields.phoneCode", "Code")}</option>
              {countries.filter((c) => c.phone_prefix).map((c) => (
                <option key={c.id} value={c.id} title={c.name}>{c.phone_prefix}</option>
              ))}
            </select>
            <Input
              data-testid="signup-phonenumber"
              className={phoneInput}
              value={form.phonenumber}
              onChange={(e) => setForm({ ...form, phonenumber: e.target.value.replace(/[^\d ]/g, "") })}
              placeholder={t("signup.fields.phonePh", "712 345 678")}
              inputMode="tel"
              autoComplete="tel-national"
              autoFocus
            />
          </div>
          {pickedPrefix && (
            <div className="text-[12px] text-[color:var(--wiz-text-3)] mt-1 inline-flex items-center gap-1">
              <Globe size={10} /> {t("signup.fields.callingCode", "Calling code")} <span className="font-mono text-[color:var(--wiz-text-2)]">{pickedPrefix.phone_prefix}</span> · {pickedPrefix.name}
            </div>
          )}
        </Field>
        <Field label={t("signup.fields.dob", "Date of birth")} hint={t("signup.fields.dobHint", "You must be at least 18 years old.")} error={errors.dob} required>
          <div className="grid grid-cols-3 gap-2">
            <select data-testid="signup-dob-day" className={baseSelectCls} value={form.dob_day} onChange={(e) => setForm({ ...form, dob_day: e.target.value })}>
              <option value="">{t("signup.fields.day", "Day")}</option>
              {range(1, 31).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select data-testid="signup-dob-month" className={baseSelectCls} value={form.dob_month} onChange={(e) => setForm({ ...form, dob_month: e.target.value })}>
              <option value="">{t("signup.fields.month", "Month")}</option>
              {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select data-testid="signup-dob-year" className={baseSelectCls} value={form.dob_year} onChange={(e) => setForm({ ...form, dob_year: e.target.value })}>
              <option value="">{t("signup.fields.year", "Year")}</option>
              {range(1925, new Date().getFullYear() - 18).reverse().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </Field>
      </div>
    </motion.div>
  );
}

/* ─── STEP 5 · Review + consents ── */
export function Step5Review({ form, setForm, errors, pickedCountry, pickedPrefix, onJumpToStep }) {
  const { t } = useTranslation();
  const titleLabel = TITLES.find(opt => opt.value === form.title);
  const titleDisplay = titleLabel ? t(`signup.titles.${titleLabel.value}`, titleLabel.label) : "";
  const accountTypeKey = form.type === "Private" ? "individual" : "corporate";
  const accountTypeLabel = t(`signup.fields.${accountTypeKey}`, form.type);
  const reviewRows = [
    { k: t("signup.review.name", "Name"),          v: `${titleDisplay} ${form.firstname} ${form.lastname}`, jump: 1, Icon: User, key: "name" },
    { k: t("signup.review.email", "Email"),        v: form.email, jump: 2, Icon: Mail, key: "email" },
    { k: t("signup.review.accountType", "Account type"),  v: accountTypeLabel + (form.type === "Corporate" ? ` · ${form.corporatename}` : ""), jump: 2, Icon: ShieldCheck, key: "accountType" },
    { k: t("signup.review.country", "Country"),    v: pickedCountry ? pickedCountry.name : "—", jump: 3, Icon: Globe, key: "country" },
    { k: t("signup.review.address", "Address"),    v: `${form.address}, ${form.city}`, jump: 3, Icon: MapPin, key: "address" },
    { k: t("signup.review.phone", "Phone"),        v: `${pickedPrefix?.phone_prefix || ""} ${form.phonenumber}`, jump: 4, Icon: Phone, key: "phone" },
    {
      k: t("signup.review.dob", "Date of birth"),
      v: form.dob_year && form.dob_month && form.dob_day
        ? `${form.dob_year}-${String(form.dob_month).padStart(2, "0")}-${String(form.dob_day).padStart(2, "0")}`
        : "—",
      jump: 4, Icon: Cake, key: "dob",
    },
  ];
  const consents = [
    { k: "agree_terms",     label: <>{t("signup.consents.termsPre", "I have read and accept the ")}<Link to="/legal/terms"    className="underline font-medium text-[color:var(--brand)] hover:text-[color:var(--wiz-text-1)]">{t("signup.consents.termsLink", "Terms of Business")}</Link>{t("signup.consents.termsSuffix", ".")}</> },
    { k: "agree_privacy",   label: <>{t("signup.consents.privacyPre", "I have read and accept the ")}<Link to="/legal/privacy"  className="underline font-medium text-[color:var(--brand)] hover:text-[color:var(--wiz-text-1)]">{t("signup.consents.privacyLink", "Privacy Policy")}</Link>{t("signup.consents.privacySuffix", ".")}</> },
    { k: "agree_risk",      label: <>{t("signup.consents.riskPre", "I understand trading involves risk · I accept the ")}<Link to="/legal/risk" className="underline font-medium text-[color:var(--brand)] hover:text-[color:var(--wiz-text-1)]">{t("signup.consents.riskLink", "Risk Disclosure")}</Link>{t("signup.consents.riskSuffix", ".")}</> },
    { k: "agree_marketing", label: t("signup.consents.marketing", "Send me market commentary, promos and product news (you can unsubscribe any time).") },
  ];

  return (
    <motion.div key="s5" {...stepMotion}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {reviewRows.map(({ k, v, jump, Icon, key }) => (
          <button
            type="button"
            key={key}
            onClick={() => onJumpToStep(jump)}
            data-testid={`signup-review-${key}`}
            className="wiz-review-row group flex items-center gap-3 py-2.5 px-3 text-left"
          >
            <Icon size={14} className="text-[color:var(--brand)] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] uppercase tracking-[0.16em] text-[color:var(--wiz-text-3)] font-semibold leading-none">
                {k}
              </div>
              <div className="text-[12.5px] text-[color:var(--wiz-text-1)] truncate mt-1 font-medium leading-tight">
                {v}
              </div>
            </div>
            <span
              className="text-[12px] font-semibold text-[color:var(--brand)] shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
              data-testid={`signup-edit-${key}`}
            >
              {t("signup.review.edit", "Edit")}
            </span>
          </button>
        ))}
      </div>

      <div
        className="rounded-xl p-4 space-y-2.5"
        style={{
          background: "color-mix(in srgb, var(--brand) 4%, transparent)",
          border: "1px solid color-mix(in srgb, var(--brand) 18%, transparent)",
        }}
      >
        <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[color:var(--wiz-text-2)]">
          {t("signup.legalSection.title", "Legal · please review before continuing")}
        </div>
        {consents.map((row) => (
          <label key={row.k} className="flex items-start gap-2.5 cursor-pointer group" data-testid={`signup-consent-${row.k}-wrap`}>
            <Checkbox
              data-testid={`signup-consent-${row.k}`}
              checked={!!form[row.k]}
              onCheckedChange={(v) => setForm({ ...form, [row.k]: !!v })}
              className="mt-0.5 border-[color:var(--wiz-line-strong)] data-[state=checked]:bg-[color:var(--brand)] data-[state=checked]:border-[color:var(--brand)] data-[state=checked]:text-[color:var(--brand-ink)]"
            />
            <span className="text-[12.5px] leading-snug text-[color:var(--wiz-text-2)] group-hover:text-[color:var(--wiz-text-1)] transition-colors">
              {row.label}
            </span>
          </label>
        ))}
        {errors.consents && (
          <div className="text-[12px] text-rose-500 dark:text-rose-300 font-medium flex items-center gap-1.5" data-testid="signup-consents-error">
            <AlertCircle size={11} className="shrink-0" />
            {errors.consents}
          </div>
        )}
      </div>
    </motion.div>
  );
}
